import { Directive, ElementRef, HostListener, Optional, Self } from '@angular/core';
import { NgControl } from '@angular/forms';

import { trimInputElement } from 'app/_utils/trim-search';

/**
 * Global rule: no leading or trailing whitespace is stored. The backend trims
 * on write; this trims the field once the user leaves it, so what they see is
 * what gets saved instead of the value silently changing after submit.
 *
 * Trimming happens on blur, never while typing. Opt a field out with
 * `data-no-trim`.
 *
 * Note: Chrome already strips padding from `type=email` / `type=url` `.value`
 * without firing `input`, which can leave ngModel padded. On blur we detect
 * that mismatch and emit `input` so the model catches up.
 */
// The compiler evaluates this statically, so it has to stay a string literal.
@Directive({
  selector: 'input[type=text]:not([data-no-trim]),'
    + 'input[type=search]:not([data-no-trim]),'
    + 'input[type=email]:not([data-no-trim]),'
    + 'input[type=tel]:not([data-no-trim]),'
    + 'input[type=url]:not([data-no-trim]),'
    + 'input:not([type]):not([data-no-trim]),'
    + 'textarea:not([data-no-trim])',
})
export class TrimInputDirective {
  constructor(
    private readonly elRef: ElementRef<HTMLInputElement | HTMLTextAreaElement>,
    @Optional() @Self() private readonly ngControl: NgControl | null,
  ) {}

  @HostListener('blur')
  onBlur(): void {
    const el = this.elRef.nativeElement;
    if (el.disabled || el.readOnly) return;

    if (trimInputElement(el)) return;

    // Browser already sanitized the DOM (common for email/url). Sync ngModel
    // when it still holds the padded string the user typed.
    const model = this.ngControl ? this.ngControl.value : undefined;
    const view = el.value || '';
    if (typeof model === 'string' && model !== view && model.trim() === view) {
      el.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }
}
